import React, { useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { useTasks } from "../context/TaskContext";
import { useJournalEntries } from "../hooks/useJournalEntries";

const toJournalDate = (dateId) => {
  if (!dateId) return new Date();
  const date = new Date(`${dateId}T12:00:00`);
  if (Number.isNaN(date.getTime())) return new Date();
  return date;
};

const displayDate = (dateId) => {
  if (!dateId) return "Unscheduled";
  const date = new Date(dateId);
  if (Number.isNaN(date.getTime())) return dateId;
  return date.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const Journals = () => {
  const { user, selectedDate, setSelectedDate, formatDateId } = useTasks();
  const { entries, entriesByDate } = useJournalEntries();
  const [newEntry, setNewEntry] = useState("");
  const [newComplete, setNewComplete] = useState(false);
  const [activeEdit, setActiveEdit] = useState(null);
  const [editValue, setEditValue] = useState("");

  const selectedEntries = entriesByDate[selectedDate] || [];

  const handleSubmitEntry = async () => {
    if (!user || !newEntry.trim()) return;
    const journalsRef = collection(db, "users", user.uid, "journals");
    try {
      await addDoc(journalsRef, {
        Complete: newComplete,
        currentTime: Timestamp.fromDate(toJournalDate(selectedDate)),
        Entry: newEntry.trim(),
      });
      setNewEntry("");
      setNewComplete(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!user) return;
    const entryDoc = doc(db, "users", user.uid, "journals", id);
    await deleteDoc(entryDoc);
  };

  const handleSaveEdit = async (id) => {
    if (!user || !editValue.trim()) return;
    const entryDoc = doc(db, "users", user.uid, "journals", id);
    await updateDoc(entryDoc, { Entry: editValue.trim() });
    setActiveEdit(null);
    setEditValue("");
  };

  const friendlyDate = useMemo(
    () => displayDate(selectedDate),
    [selectedDate]
  );

  const handleJumpToToday = () => {
    const today = formatDateId(new Date());
    setSelectedDate(today, { ensureList: false });
  };

  return (
    <div className="app-layout">
      <header className="page-header">
        <div className="page-header__content">
          <h1>Daily journal</h1>
          <p>
            Use the calendar to pick a day and capture reflections, wins, and
            lessons learned.
          </p>
        </div>
        <div className="page-header__aside">
          <span className="badge">{entries.length} entries</span>
        </div>
      </header>

      <div className="journal-wrapper">
        <aside className="journal-sidebar">
          <h2 style={{ marginTop: 0 }}>New entry</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            Journaling for <strong>{friendlyDate}</strong>
          </p>
          <div className="journal-form">
            <textarea
              rows={6}
              placeholder="What happened today?"
              value={newEntry}
              onChange={(e) => setNewEntry(e.target.value)}
            />
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={newComplete}
                onChange={(e) => setNewComplete(e.target.checked)}
              />
              Mark as complete
            </label>
            <div className="cluster" style={{ justifyContent: "space-between" }}>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={handleJumpToToday}
              >
                Jump to today
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleSubmitEntry}
                disabled={!newEntry.trim()}
              >
                Save entry
              </button>
            </div>
          </div>
        </aside>

        <section className="page-section" style={{ minHeight: "420px" }}>
          <div className="page-section__headline">
            <h2>Entries on {friendlyDate}</h2>
            <span className="pill">
              {selectedEntries.length} {selectedEntries.length === 1 ? "entry" : "entries"}
            </span>
          </div>

          {selectedEntries.length ? (
            <div className="journal-list">
              {selectedEntries.map((journal) => {
                const timestamp = journal.currentTime?.toDate?.();
                const formattedDate = timestamp
                  ? timestamp.toLocaleTimeString(undefined, {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "Pending";

                const isEditing = activeEdit === journal.id;

                return (
                  <div key={journal.id} className="journal-card">
                    <div className="journal-card__top">
                      <div className="stack stack--dense" style={{ flex: 1 }}>
                        <span className="journal-card__time">
                          {formattedDate}
                        </span>
                        {isEditing ? (
                          <textarea
                            rows={4}
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                          />
                        ) : (
                          <div className="journal-card__body">
                            {journal.Entry}
                          </div>
                        )}
                      </div>
                      <span className="pill">
                        {journal.Complete ? "Complete" : "Draft"}
                      </span>
                    </div>

                    <div className="journal-card__actions">
                      {isEditing ? (
                        <>
                          <button
                            className="btn btn--primary"
                            onClick={() => handleSaveEdit(journal.id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn--ghost"
                            onClick={() => setActiveEdit(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn--ghost"
                            onClick={() => {
                              setActiveEdit(journal.id);
                              setEditValue(journal.Entry || "");
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn--danger"
                            onClick={() => handleDelete(journal.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <strong>No journal entries for this day</strong>
              Use the form on the left to capture your notes.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Journals;
